import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Product, ProductCategory, Cart, PaginatedResponse, ShippingMethod, Order, Address } from '../types';
import { fetchRuntimeConfig } from './config';

// Runtime configuration support provided via fetchRuntimeConfig()
// Window.__ENV__ type declaration moved to vite-env.d.ts

// Cached base query instance - created once after config loads
let cachedBaseQuery: ReturnType<typeof fetchBaseQuery> | null = null;
let cachedConfig: Awaited<ReturnType<typeof fetchRuntimeConfig>> | null = null;

// Custom baseQuery that handles runtime configuration
const dynamicBaseQuery: ReturnType<typeof fetchBaseQuery> = async (args, api, extraOptions) => {
  if (!cachedConfig) {
    cachedConfig = await fetchRuntimeConfig();
  }
  const config = cachedConfig;

  if (!cachedBaseQuery) {
    const baseUrl = `${config.medusa.backendUrl}/store`;
    cachedBaseQuery = fetchBaseQuery({
      baseUrl,
      prepareHeaders: (headers) => {
        if (config.medusa.publishableKey) {
          headers.set('x-publishable-api-key', config.medusa.publishableKey);
        }
        headers.set('Content-Type', 'application/json');
        return headers;
      },
      paramsSerializer: (params: Record<string, unknown>) => {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
          if (value === undefined || value === null) continue;
          if (Array.isArray(value)) {
            for (const item of value) {
              searchParams.append(`${key}[]`, String(item));
            }
          } else {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      },
    });
  }

  // Inject region_id into product queries if available
  if (typeof args !== 'string' && args.url?.includes('/products')) {
    const regionId = config.medusa.regionId;
    if (regionId) {
      args.params = {
        ...args.params,
        region_id: regionId,
      };
    }
  }

  return cachedBaseQuery(args, api, extraOptions);
};

export const medusaApi = createApi({
  reducerPath: 'medusaApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['Product', 'Cart', 'Customer', 'Order', 'Category'],
  endpoints: (builder) => ({
    // Products
    // Product Categories
    getProductCategories: builder.query<{ product_categories: ProductCategory[] }, {
      parent_category_id?: string | null;
      include_descendants_tree?: boolean;
      fields?: string;
    }>({
      query: (params = {}) => ({
        url: '/product-categories',
        params: {
          include_descendants_tree: true,
          fields: 'id,name,handle,description,rank,is_active,is_internal,parent_category_id,*category_children',
          ...params,
        },
      }),
      providesTags: ['Category'],
    }),

    getProductCategoryByHandle: builder.query<{ product_categories: ProductCategory[] }, string>({
      query: (handle) => ({
        url: '/product-categories',
        params: {
          handle,
          include_descendants_tree: true,
          fields: 'id,name,handle,description,rank,is_active,is_internal,parent_category_id,*category_children',
        },
      }),
      providesTags: ['Category'],
    }),

    // Products
    getProducts: builder.query<PaginatedResponse<Product>, {
      offset?: number;
      limit?: number;
      q?: string;
      collection_id?: string;
      category_id?: string[];
      region_id?: string;
    }>({
      query: (params = {}) => ({
        url: '/products',
        params: {
          offset: 0,
          limit: 20,
          fields: '*variants.calculated_price',
          ...params,
        },
      }),
      providesTags: ['Product'],
    }),

    getProduct: builder.query<{ product: Product }, { id: string; region_id?: string }>({
      query: ({ id, region_id }) => ({
        url: `/products/${id}`,
        params: {
          fields: '*variants.calculated_price',
          ...(region_id && { region_id }),
        },
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Product', id }],
    }),

    // Cart
    // Get available regions first
    getRegions: builder.query<{ regions: Array<{ id: string; name: string; currency_code: string }> }, void>({
      query: () => '/regions',
    }),

    getRegion: builder.query<{
      region: {
        id: string;
        name: string;
        currency_code: string;
        countries: Array<{
          id: string;
          iso_2: string;
          iso_3: string;
          num_code: string;
          name: string;
          display_name: string;
        }>;
      }
    }, string>({
      query: (regionId) => ({
        url: `/regions/${regionId}`,
        params: {
          fields: 'id,name,currency_code,*countries',
        },
      }),
    }),

    createCart: builder.mutation<{ cart: Cart }, { region_id?: string }>({
      query: ({ region_id } = {}) => ({
        url: '/carts',
        method: 'POST',
        body: region_id ? { region_id } : {},
      }),
      invalidatesTags: ['Cart'],
    }),

    getCart: builder.query<{ cart: Cart }, string>({
      query: (cartId) => ({
        url: `/carts/${cartId}`,
        params: {
          fields: '*items.product,*items.variant',
        },
      }),
      providesTags: (_result, _error, cartId) => [{ type: 'Cart', id: cartId }],
    }),

    addToCart: builder.mutation<{ cart: Cart }, {
      cartId: string;
      variantId: string;
      quantity: number;
    }>({
      query: ({ cartId, variantId, quantity }) => ({
        url: `/carts/${cartId}/line-items`,
        method: 'POST',
        body: {
          variant_id: variantId,
          quantity,
        },
      }),
      invalidatesTags: (_result, _error, { cartId }) => [{ type: 'Cart', id: cartId }],
    }),

    updateCart: builder.mutation<{ cart: Cart }, {
      cartId: string;
      email?: string;
      shipping_address?: Address;
      billing_address?: Address;
    }>({
      query: ({ cartId, ...body }) => ({
        url: `/carts/${cartId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { cartId }) => [{ type: 'Cart', id: cartId }],
    }),

    updateCartItem: builder.mutation<{ cart: Cart }, {
      cartId: string;
      lineId: string;
      quantity: number;
    }>({
      query: ({ cartId, lineId, quantity }) => ({
        url: `/carts/${cartId}/line-items/${lineId}`,
        method: 'POST',
        body: { quantity },
      }),
      invalidatesTags: (_result, _error, { cartId }) => [{ type: 'Cart', id: cartId }],
    }),

    removeFromCart: builder.mutation<{ cart: Cart }, {
      cartId: string;
      lineId: string;
    }>({
      query: ({ cartId, lineId }) => ({
        url: `/carts/${cartId}/line-items/${lineId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { cartId }) => [{ type: 'Cart', id: cartId }],
    }),

    // Checkout & Orders
    addShippingMethod: builder.mutation<
      { cart: Cart },
      { cartId: string; optionId: string }
    >({
      query: ({ cartId, optionId }) => ({
        url: `/carts/${cartId}/shipping-methods`,
        method: 'POST',
        body: {
          option_id: optionId,
        },
      }),
      invalidatesTags: (_result, _error, { cartId }) => [{ type: 'Cart', id: cartId }],
    }),

    getShippingMethods: builder.query<{ shipping_options: ShippingMethod[] }, string>(
      {
        query: (cartId) => ({
          url: '/shipping-options',
          params: {
            cart_id: cartId,
          },
        }),
        transformResponse: (response: any) => {
          // Transform backend shipping options to frontend ShippingMethod format
          const shipping_options = (response.shipping_options || []).map((option: any) => ({
            id: option.id,
            name: option.name,
            price: option.amount || option.calculated_price?.calculated_amount || 0,
            currency_code: option.calculated_price?.currency_code || option.currency_code || 'sek',
            description: option.type?.description,
          }));
          return { shipping_options };
        },
        providesTags: (_result, _error, cartId) => [{ type: 'Cart', id: cartId }],
      }
    ),

    // Payment Providers - Get available payment providers for a region
    getPaymentProviders: builder.query<{ payment_providers: Array<{ id: string }> }, string>({
      query: (regionId) => ({
        url: '/payment-providers',
        params: {
          region_id: regionId,
        },
      }),
    }),

    // Payment Collections - Medusa 2.x payment flow
    // Step 1: Create a payment collection for the cart
    createPaymentCollection: builder.mutation<
      { payment_collection: { id: string; cart_id: string; amount: number; currency_code: string } },
      { cartId: string }
    >({
      query: ({ cartId }) => ({
        url: '/payment-collections',
        method: 'POST',
        body: {
          cart_id: cartId,
        },
      }),
      invalidatesTags: (_result, _error, { cartId }) => [{ type: 'Cart', id: cartId }],
    }),

    // Step 2: Initialize a payment session on the payment collection
    initializePaymentSession: builder.mutation<
      {
        payment_collection: {
          id: string;
          payment_sessions: Array<{
            id: string;
            provider_id: string;
            status: string;
            data?: { client_secret?: string };
          }>;
        };
      },
      { paymentCollectionId: string; providerId: string; data?: Record<string, unknown>; context?: Record<string, unknown> }
    >({
      query: ({ paymentCollectionId, providerId, data, context }) => ({
        url: `/payment-collections/${paymentCollectionId}/payment-sessions`,
        method: 'POST',
        body: {
          provider_id: providerId,
          ...(data && { data }),
          ...(context && { context }),
        },
      }),
    }),

    // Legacy: createPaymentSession (deprecated, use createPaymentCollection + initializePaymentSession)
    createPaymentSession: builder.mutation<
      { payment_session: any },
      { cartId: string; providerId: string }
    >({
      query: ({ cartId, providerId }) => ({
        url: `/carts/${cartId}/payment-sessions`,
        method: 'POST',
        body: {
          provider_id: providerId,
        },
      }),
      invalidatesTags: (_result, _error, { cartId }) => [{ type: 'Cart', id: cartId }],
    }),

    // Step 3: Complete the cart (after payment session is authorized)
    completeCart: builder.mutation<
      { type: 'order' | 'cart'; order?: Order; cart?: Cart; error?: string },
      { cartId: string }
    >({
      query: ({ cartId }) => ({
        url: `/carts/${cartId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: ['Cart', 'Order'],
    }),

    createOrder: builder.mutation<{ order: Order }, {
      email: string;
      shipping_address: Address;
      billing_address: Address;
      items: Array<{ variant_id: string; quantity: number }>;
      shipping_method_id?: string;
      payment_method?: any;
    }>({
      query: (body) => ({
        url: '/orders',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Order'],
    }),

    getOrder: builder.query<{ order: Order }, string>({
      query: (orderId) => ({
        url: `/orders/${orderId}`,
        params: {
          fields: '*items.product,*items.variant',
        },
      }),
      providesTags: (_result, _error, orderId) => [{ type: 'Order', id: orderId }],
    }),
  }),
});

export const {
  useGetProductCategoriesQuery,
  useGetProductCategoryByHandleQuery,
  useGetProductsQuery,
  useGetProductQuery,
  useGetRegionsQuery,
  useGetRegionQuery,
  useCreateCartMutation,
  useGetCartQuery,
  useAddToCartMutation,
  useUpdateCartMutation,
  useUpdateCartItemMutation,
  useRemoveFromCartMutation,
  useAddShippingMethodMutation,
  useGetShippingMethodsQuery,
  useGetPaymentProvidersQuery,
  useCreatePaymentCollectionMutation,
  useInitializePaymentSessionMutation,
  useCreatePaymentSessionMutation,
  useCompleteCartMutation,
  useCreateOrderMutation,
  useGetOrderQuery,
} = medusaApi;
